import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import { trackErrorEvent, trackEvent } from "../utils/analytics";

/**
 * Re-runs the department's wage breakdown over its whole history (`include_past=true`).
 *
 * Raised from Department Configuration (Rates & Rules, the PPD-override prompt). Team → Wage Settings
 * does not call it: those saves queue the same full-history recalc themselves.
 *
 * `enqueued: false` is a SUCCESS, not a failure: the backend returns it when a pending recalc row for
 * this department already exists — e.g. the future-only one a Department Configuration save queues.
 * That existing row is upgraded to `include_past=true` instead of a second one being queued, so the
 * past data the user asked for is covered either way.
 */
export function useRunWageBreakdown() {
	const {
		chosenDepartment: { department_id },
		showErrorSnackbar,
	} = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();

	return useMutation<void, AxiosError<{ detail?: string }>>({
		mutationFn: async () => {
			const response = await backendService.runWageBreakdown(department_id);
			if (response.status >= 400) {
				throw new Error(response.statusText);
			}
		},
		onSuccess: () => {
			trackEvent("Department configuration - run wage breakdown", { department_id });
			showSnackbar(t("department.config.rates.runWageBreakdown.success"), "success");
		},
		onError: (error) => {
			const detail = error.response?.data?.detail ?? error.message;
			trackErrorEvent("Department configuration - run wage breakdown failed", {
				department_id,
				error: detail,
			});
			showErrorSnackbar(detail, {}, error);
		},
		throwOnError: false,
	});
}
